import React, { useEffect, useState } from "react";
import { useNotification } from "../../../../NotificationContext";
import LoaderComponent from "../../../../components/LoaderComponent";
import { format } from "date-fns";
import { getIPDVisitBilling, getVisitTransactions, createIPDInvoice } from "../../../../apis/visit";
import { processPayment } from "../../../../apis/payment";
import { savePatientTransaction } from "../../../../apis/patientTransaction";
import { getPatientTransactions } from "../../../../apis/patientTransaction";
import PaymentModal, { PaymentData } from "../../../../components/PaymentModal";

interface IPDBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitId: number;
  patientId: number;
}

interface BillingData {
  pharmacy: Array<{
    id: number;
    saleNumber: string;
    saleDate: string;
    items: Array<{
      name: string;
      quantity: number;
      sellingPrice: number;
      discountPercentage: number;
      taxPercentage: number;
      total: number;
    }>;
    totalAmount: number;
    discountAmount: number;
    taxAmount: number;
    netAmount: number;
  }>;
  labOrders: Array<{
    id: number;
    orderNumber: string;
    orderDate: string;
    items: Array<{
      name: string;
      fee: number;
    }>;
    totalAmount: number;
  }>;
  services: Array<{
    id: number;
    name: string;
    fee: number;
    quantity: number;
    total: number;
  }>;
  roomCharges?: Array<{
    id: number;
    roomName: string;
    roomType: string;
    fromDate: string;
    toDate: string;
    durationDays: number;
    durationHours: number;
    charge: number;
  }>;
  patientBalance?: number;
}

type NavigationItem = "summary" | "billItems" | "payments";

const IPDBillingModal: React.FC<IPDBillingModalProps> = ({
  isOpen,
  onClose,
  visitId,
  patientId,
}) => {
  const { showNotification } = useNotification();
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeNav, setActiveNav] = useState<NavigationItem>("summary");
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    pharmacy: false,
    labOrders: false,
    services: false,
    roomCharges: false,
  });
  const [visitTransactions, setVisitTransactions] = useState<any[]>([]);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
  const [rateOverrides, setRateOverrides] = useState<Record<string, number>>({});
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [advanceBalance, setAdvanceBalance] = useState<number | null>(null);
  const [customItems, setCustomItems] = useState<
    Array<{ id: string; name: string; quantity: number; rate: number }>
  >([]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentContext, setPaymentContext] = useState<"selected" | "outstanding">("outstanding");

  const hasInvoice = Boolean(invoiceData?.invoice);

  const buildRateOverrides = (data: BillingData) => {
    const overrides: Record<string, number> = {};
    data.pharmacy.forEach((sale) => {
      overrides[`pharmacy-${sale.id}`] = sale.netAmount || 0;
    });
    data.labOrders.forEach((order) => {
      overrides[`lab-${order.id}`] = order.totalAmount || 0;
    });
    data.services.forEach((service) => {
      overrides[`service-${service.id}`] = service.total || 0;
    });
    data.roomCharges?.forEach((room, idx) => {
      overrides[`room-${room.id}-${idx}`] = room.charge || 0;
    });
    return overrides;
  };

  const addCustomItem = () => {
    setCustomItems((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: "",
        quantity: 1,
        rate: 0,
      },
    ]);
  };

  const updateCustomItem = (
    id: string,
    updates: Partial<{ name: string; quantity: number; rate: number }>
  ) => {
    setCustomItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const removeCustomItem = (id: string) => {
    setCustomItems((prev) => prev.filter((item) => item.id !== id));
  };

  useEffect(() => {
    if (isOpen && visitId) {
      fetchBillingData();
      if (activeNav === "payments") {
        fetchPaymentData();
      }
    }
  }, [isOpen, visitId, activeNav]);

  useEffect(() => {
    if (billingData && !hasInvoice) {
      setRateOverrides(buildRateOverrides(billingData));
    }
  }, [billingData, hasInvoice]);

  const fetchBillingData = async () => {
    setIsLoading(true);
    try {
      const response = await getIPDVisitBilling(visitId);
      setBillingData(response as BillingData);
    } catch (error: any) {
      console.error("Failed to fetch billing data:", error);
      showNotification(
        "error",
        "Error",
        error?.response?.data?.message || "Failed to load billing data",
        "topRight"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPaymentData = async () => {
    try {
      // Fetch invoice data (includes visit-level transactions and allocations)
      const invoiceRes: any = await getVisitTransactions(visitId);
      const invoicePayload = invoiceRes?.data || null;
      setInvoiceData(invoicePayload);
      setVisitTransactions(invoicePayload?.invoice?.transactions || []);
      const balanceFromInvoice =
        invoicePayload?.patientBalance ??
        0;
        setAdvanceBalance(balanceFromInvoice);
      
    } catch (error: any) {
      console.error("Failed to fetch payment data:", error);
    }
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) {
      return "₹0.00";
    }
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) {
      return "₹0.00";
    }
    return `₹${num.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(date, "dd MMM yyyy hh:mm a");
    } catch (error) {
      return "-";
    }
  };

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(date, "dd MMM yyyy, HH:mm");
    } catch (error) {
      return "-";
    }
  };

  const calculatePharmacyTotal = () => {
    if (!billingData) return 0;
    return billingData.pharmacy.reduce((sum, sale) => sum + (sale.netAmount || 0), 0);
  };

  const calculateLabTotal = () => {
    if (!billingData) return 0;
    return billingData.labOrders.reduce((sum, order) => {
      if (order.totalAmount && order.totalAmount > 0) {
        return sum + order.totalAmount;
      }
      const itemsTotal = order.items?.reduce((itemSum, item) => itemSum + (item.fee || 0), 0) || 0;
      return sum + itemsTotal;
    }, 0);
  };

  const calculateServicesTotal = () => {
    if (!billingData) return 0;
    return billingData.services.reduce((sum, service) => sum + (service.total || 0), 0);
  };

  const calculateRoomChargesTotal = () => {
    if (!billingData || !billingData.roomCharges) return 0;
    return billingData.roomCharges.reduce((sum, room) => sum + (room.charge || 0), 0);
  };

  const calculateGrandTotal = () => {
    return calculatePharmacyTotal() + calculateLabTotal() + calculateServicesTotal() + calculateRoomChargesTotal();
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const calculateOutstanding = () => {
    const grandTotal = calculateGrandTotal();
    const totalAllocated = invoiceData?.invoice?.paidAmount || 0;
    return Math.max(0, grandTotal - totalAllocated);
  };

  const calculateUnallocatedBalance = () => {
    const totalTransactions = visitTransactions.reduce(
      (sum, txn) => sum + (parseFloat(txn.amount) || 0),
      0
    );
    console.log({totalTransactions});
    const totalAllocated = invoiceData?.invoice?.paymentAllocations?.reduce(
      (sum: number, alloc: any) => sum + (parseFloat(alloc.allocatedAmount) || 0),
      0
    ) || 0;
    return Math.max(0, totalTransactions - totalAllocated);
  };

  const getAdvanceBalance = () => {
    console.log({advanceBalance});
    if (advanceBalance !== null && advanceBalance !== undefined) {
      return advanceBalance;
    }
    console.log({billingData});
    if (billingData?.patientBalance !== undefined && billingData?.patientBalance !== null) {
      return billingData.patientBalance;
    }
    return calculateUnallocatedBalance();
  };

  const getRateValue = (key: string, fallback: number) => {
    const value = rateOverrides[key];
    if (value === undefined || value === null || Number.isNaN(value)) {
      return fallback;
    }
    return value;
  };

  const calculateEditableTotal = () => {
    if (!billingData) return 0;
    let total = 0;
    billingData.pharmacy.forEach((sale) => {
      total += getRateValue(`pharmacy-${sale.id}`, sale.netAmount || 0);
    });
    billingData.labOrders.forEach((order) => {
      total += getRateValue(`lab-${order.id}`, order.totalAmount || 0);
    });
    billingData.services.forEach((service) => {
      total += getRateValue(`service-${service.id}`, service.total || 0);
    });
    billingData.roomCharges?.forEach((room, idx) => {
      total += getRateValue(`room-${room.id}-${idx}`, room.charge || 0);
    });
    customItems.forEach((item) => {
      total += (item.quantity || 0) * (item.rate || 0);
    });
    return total;
  };

  const handleAdvanceSave = async (paymentData: PaymentData) => {
    if (!patientId) return;

    try {
      const paymentModes = [
        { mode: "cash", amount: paymentData.cash },
        { mode: "upi", amount: paymentData.upi },
        { mode: "card", amount: paymentData.card },
        { mode: "cheque", amount: paymentData.cheque },
        { mode: "online", amount: paymentData.online },
      ];

      const savePromises = paymentModes
        .filter((p) => p.amount > 0)
        .map((payment) =>
          savePatientTransaction({
            patientId,
            amount: payment.amount,
            paymentMode: payment.mode,
            note: paymentData.paymentNote || "",
            date: format(new Date(), "yyyy-MM-dd HH:mm").toString(),
          })
        );

      await Promise.all(savePromises);
      showNotification("success", "Advance collected successfully", "", "topRight");
      setIsAdvanceModalOpen(false);
      fetchPaymentData();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to collect advance",
        err?.message || "",
        "topRight"
      );
    }
  };

  const buildInvoiceItems = () => {
    if (!billingData) return [];
    const items: Array<{
      itemType: string;
      refId: number;
      quantity: number;
      description: string;  
      unitPrice: number;
      amount: number;
      name?: string;
    }> = [];

    billingData.pharmacy.forEach((sale) => {
      const amount = getRateValue(`pharmacy-${sale.id}`, sale.netAmount || 0);
      items.push({
        itemType: "pharmacy",
        refId: sale.id,
        quantity: 1,
        description: sale.items.map((item) => item.name).join(", "),
        unitPrice: amount,
        amount,
      });
    });

    billingData.labOrders.forEach((order) => {
      const amount = getRateValue(`lab-${order.id}`, order.totalAmount || 0);
      items.push({
        itemType: "lab",
        refId: order.id,
        quantity: 1,
        description: `Lab Order #${order.orderNumber} - ${order.orderDate}`,
        unitPrice: amount,
        amount,
      });
    });

    billingData.services.forEach((service) => {
      const amount = getRateValue(`service-${service.id}`, service.total || 0);
      const quantity = service.quantity || 1;
      items.push({
        itemType: "service",
        refId: service.id,
        quantity,
        description: `Service #${service.id} - ${service.name}`,
        unitPrice: quantity > 0 ? amount / quantity : amount,
        amount,
      });
    });

    billingData.roomCharges?.forEach((room, idx) => {
      const amount = getRateValue(`room-${room.id}-${idx}`, room.charge || 0);
      const quantity =  1;
      items.push({
        itemType: "room",
        description: `Room ${room.roomName} (${room.roomType})`,
        refId: room.id,
        quantity,
        unitPrice: quantity > 0 ? amount / quantity : amount,
        amount,
      });
    });

    customItems
      .filter((item) => item.name.trim() && item.quantity > 0)
      .forEach((item) => {
        const quantity = item.quantity || 1;
        const rate = item.rate || 0;
        items.push({
          itemType: "custom",
          description: item.name.trim(),
          refId: 0,
          name: item.name.trim(),
          quantity,
          unitPrice: rate,
          amount: quantity * rate,
        });
      });

    return items;
  };

  const handleGenerateInvoice = async () => {
    if (!billingData || isGeneratingInvoice) return;
    const items = buildInvoiceItems();
    if (items.length === 0) {
      showNotification("warning", "No bill items available", "", "topRight");
      return;
    }
    setIsGeneratingInvoice(true);
    try {
      await createIPDInvoice(visitId, { items });
      showNotification("success", "Bill generated successfully", "", "topRight");
      fetchPaymentData();
      fetchBillingData();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to generate bill",
        err?.response?.data?.message || err?.message || "",
        "topRight"
      );
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleItemSelection = (itemKey: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  const getSelectedItemsTotal = () => {
    if (!billingData) return 0;
    let total = 0;

    selectedItems.forEach((key) => {
      const [type, id] = key.split("-");
      if (type === "pharmacy") {
        const sale = billingData.pharmacy.find((s) => s.id === parseInt(id));
        if (sale) total += sale.netAmount || 0;
      } else if (type === "lab") {
        const order = billingData.labOrders.find((o) => o.id === parseInt(id));
        if (order) total += order.totalAmount || 0;
      } else if (type === "service") {
        const service = billingData.services.find((s) => s.id === parseInt(id));
        if (service) total += service.total || 0;
      } else if (type === "room") {
        const room = billingData.roomCharges?.find((r) => r.id === parseInt(id));
        if (room) total += room.charge || 0;
      }
    });

    return total;
  };

  const buildPaymentSourcesFromSelection = () => {
    const sourceIds: number[] = [];
    const sourceAmounts: Record<number, number> = {};

    selectedItems.forEach((key) => {
      const [type, id] = key.split("-");
      const numId = parseInt(id);

      if (type === "pharmacy") {
        sourceIds.push(numId);
        const sale = billingData?.pharmacy.find((s) => s.id === numId);
        if (sale) sourceAmounts[numId] = sale.netAmount || 0;
      } else if (type === "lab") {
        sourceIds.push(numId);
        const order = billingData?.labOrders.find((o) => o.id === numId);
        if (order) sourceAmounts[numId] = order.totalAmount || 0;
      }
    });

    const hasServicesOrRooms = Array.from(selectedItems).some(
      (key) => key.startsWith("service-") || key.startsWith("room-")
    );

    if (hasServicesOrRooms) {
      sourceIds.push(visitId);
    }

    return {
      sourceIds: sourceIds.length > 0 ? sourceIds : [visitId],
      sourceAmounts,
    };
  };

  const handleProcessPayment = async () => {
    if (selectedItems.size === 0) {
      showNotification("warning", "Please select items to pay", "", "topRight");
      return;
    }

    setIsProcessingPayment(true);
    try {
      const { sourceIds, sourceAmounts } = buildPaymentSourcesFromSelection();

      const payload: any = {
        sourceType: "visit",
        sourceIds,
        payments: [
          {
            amount: getSelectedItemsTotal(),
            paymentMode: "cash",
            paymentDate: new Date().toISOString().split("T")[0],
            allocationType: "manual",
          },
        ],
        sourceAmounts: sourceAmounts,
      };

      await processPayment(payload);
      showNotification("success", "Payment processed successfully", "", "topRight");
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      console.error("Failed to process payment:", error);
      showNotification(
        "error",
        "Failed to process payment",
        error?.response?.data?.message || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const openPaymentModal = (context: "selected" | "outstanding") => {
    if (!hasInvoice) return;
    const outstanding = calculateOutstanding();
    if (outstanding <= 0) {
      showNotification("info", "Invoice already paid", "", "topRight");
      return;
    }
    if (context === "selected" && selectedItems.size === 0) {
      showNotification("warning", "Please select items to pay", "", "topRight");
      return;
    }
    setPaymentContext(context);
    setIsPaymentModalOpen(true);
  };

  const handleInvoicePaymentSave = async (paymentData: PaymentData) => {
    if (!hasInvoice) return;
    const selectedTotal = getSelectedItemsTotal();
    const outstanding = calculateOutstanding();
    const totalDue = paymentContext === "selected" ? selectedTotal : outstanding;
    const maxPayable = Math.min(totalDue, outstanding);
    if (maxPayable <= 0) {
      showNotification("warning", "Nothing due for payment", "", "topRight");
      return;
    }

    const paymentModes = [
      { mode: "cash", amount: paymentData.cash },
      { mode: "upi", amount: paymentData.upi },
      { mode: "card", amount: paymentData.card },
      { mode: "cheque", amount: paymentData.cheque },
      { mode: "online", amount: paymentData.online },
    ];
    const payments: Array<{
      amount: number;
      paymentMode: string;
      paymentDate: string;
      notes?: string;
      allocationType?: "manual" | "auto";
      allocationNotes?: string;
    }> = [];
    const currentDate = new Date().toISOString().split("T")[0];
    paymentModes
      .filter((p) => p.amount > 0)
      .forEach((payment) => {
        payments.push({
          amount: payment.amount,
          paymentMode: payment.mode,
          paymentDate: currentDate,
          notes: paymentData.paymentNote || undefined,
          allocationType: "manual",
        });
      });

    const advanceUsed = paymentData.advanceUsed || 0;
    if (advanceUsed > 0) {
      payments.push({
        amount: advanceUsed,
        paymentMode: "cash",
        paymentDate: currentDate,
        notes: paymentData.paymentNote || undefined,
        allocationType: "manual",
        allocationNotes: "Advance allocation",
      });
    }

    if (payments.length === 0) {
      showNotification("warning", "Please enter a payment amount", "", "topRight");
      return;
    }

    const totalPayment = payments.reduce(
      (sum, payment) => sum + (parseFloat(String(payment.amount)) || 0),
      0
    );
    if (totalPayment > maxPayable) {
      showNotification(
        "warning",
        "Payment exceeds amount due",
        `Max allowed: ${formatCurrency(maxPayable)}`,
        "topRight"
      );
      return;
    }

    setIsProcessingPayment(true);
    try {
      const { sourceIds, sourceAmounts } =
        paymentContext === "selected" && selectedItems.size > 0
          ? buildPaymentSourcesFromSelection()
          : { sourceIds: [visitId], sourceAmounts: {} };

      const payload: any = {
        sourceType: "visit",
        sourceIds,
        payments,
      };
      if (Object.keys(sourceAmounts).length > 0) {
        payload.sourceAmounts = sourceAmounts;
      }

      await processPayment(payload);
      showNotification("success", "Payment processed successfully", "", "topRight");
      setIsPaymentModalOpen(false);
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      showNotification(
        "error",
        "Failed to process payment",
        error?.response?.data?.error?.details || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleUseAdvance = async () => {
    if (!hasInvoice) return;
    const advanceBalance = getAdvanceBalance();
    const outstanding = calculateOutstanding();
    const selectedTotal = getSelectedItemsTotal();
    const targetAmount = selectedItems.size > 0 ? selectedTotal : outstanding;
    const amountToApply = Math.min(advanceBalance, targetAmount);

    if (amountToApply <= 0) {
      showNotification("warning", "No advance available to allocate", "", "topRight");
      return;
    }

    setIsProcessingPayment(true);
    try {
      const { sourceIds, sourceAmounts } =
        selectedItems.size > 0 ? buildPaymentSourcesFromSelection() : {
          sourceIds: [visitId],
          sourceAmounts: {},
        };

      const payload: any = {
        sourceType: "visit",
        sourceIds,
        payments: [
          {
            amount: amountToApply,
            paymentMode: "cash",
            paymentDate: new Date().toISOString().split("T")[0],
            allocationType: "manual",
            allocationNotes: "Advance allocation",
          },
        ],
        sourceAmounts: sourceAmounts,
      };

      await processPayment(payload);
      showNotification("success", "Advance allocated successfully", "", "topRight");
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      console.error("Failed to allocate advance:", error);
      showNotification(
        "error",
        "Failed to allocate advance",
        error?.response?.data?.message || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const renderSummaryView = () => {
    if (!billingData) return null;

    return (
      <div className="space-y-4">
        {/* Pharmacy Section */}
        {billingData.pharmacy.length > 0 && (
          <div className="border rounded-lg">
            <div
              className="p-4 bg-gray-50 border-b cursor-pointer flex justify-between items-center"
              onClick={() => toggleSection("pharmacy")}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Pharmacy Orders
              </h3>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculatePharmacyTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${
                    expandedSections.pharmacy ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.pharmacy && (
              <div className="p-4 space-y-4">
                {billingData.pharmacy.map((sale) => (
                  <div key={sale.id} className="border-b pb-4 last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <span className="font-medium text-gray-700">
                          Sale #{sale.saleNumber}
                        </span>
                        <span className="ml-2 text-sm text-gray-500">
                          {formatDate(sale.saleDate)}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(sale.netAmount)}
                      </span>
                    </div>
                    <div className="ml-4 mt-2 space-y-1">
                      {sale.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-sm text-gray-600"
                        >
                          <span>
                            {item.name} × {item.quantity}
                          </span>
                          <span>{formatCurrency(item.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lab Orders Section */}
        {billingData.labOrders.length > 0 && (
          <div className="border rounded-lg">
            <div
              className="p-4 bg-gray-50 border-b cursor-pointer flex justify-between items-center"
              onClick={() => toggleSection("labOrders")}
            >
              <h3 className="text-lg font-semibold text-gray-900">Lab Orders</h3>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateLabTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${
                    expandedSections.labOrders ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.labOrders && (
              <div className="p-4 space-y-4">
                {billingData.labOrders.map((order) => (
                  <div key={order.id} className="border-b pb-4 last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <span className="font-medium text-gray-700">
                          Order #{order.orderNumber}
                        </span>
                        <span className="ml-2 text-sm text-gray-500">
                          {formatDate(order.orderDate)}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(order.totalAmount)}
                      </span>
                    </div>
                    <div className="ml-4 mt-2 space-y-1">
                      {order.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-sm text-gray-600"
                        >
                          <span>{item.name}</span>
                          <span>{formatCurrency(item.fee)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Services Section */}
        {billingData.services.length > 0 && (
          <div className="border rounded-lg">
            <div
              className="p-4 bg-gray-50 border-b cursor-pointer flex justify-between items-center"
              onClick={() => toggleSection("services")}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Visit Services
              </h3>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateServicesTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${
                    expandedSections.services ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.services && (
              <div className="p-4">
                <div className="space-y-2">
                  {billingData.services.map((service) => (
                    <div
                      key={service.id}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-gray-700">
                        {service.name} {service.quantity > 1 && `× ${service.quantity}`}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(service.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Room Charges Section */}
        {billingData.roomCharges && billingData.roomCharges.length > 0 && (
          <div className="border rounded-lg">
            <div
              className="p-4 bg-gray-50 border-b cursor-pointer flex justify-between items-center"
              onClick={() => toggleSection("roomCharges")}
            >
              <h3 className="text-lg font-semibold text-gray-900">Room Charges</h3>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateRoomChargesTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${
                    expandedSections.roomCharges ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.roomCharges && (
              <div className="p-4 space-y-4">
                {billingData.roomCharges.map((room, idx) => {
                  const durationDays = room.durationDays ?? 0;
                  const durationHours = room.durationHours ?? 0;
                  const isValidDuration =
                    !isNaN(durationDays) &&
                    !isNaN(durationHours) &&
                    durationDays >= 0 &&
                    durationHours >= 0;

                  return (
                    <div
                      key={`room-${room.id}-${idx}-${room.fromDate}`}
                      className="border-b pb-4 last:border-b-0 last:pb-0"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="font-medium text-gray-700">
                            {room.roomName} ({room.roomType})
                          </span>
                          <div className="text-sm text-gray-500 mt-1">
                            {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                          </div>
                          {isValidDuration && (
                            <div className="text-xs text-gray-400 mt-1">
                              Duration: {Math.ceil(room.durationDays)} day(s) ({room.durationHours} hours)
                            </div>
                          )}
                        </div>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(room.charge)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Grand Total */}
        <div className="border-2 rounded-lg bg-gray-50">
          <div className="p-4">
            <div className="flex justify-between items-center">
              <span className="text-xl font-bold text-gray-900">Grand Total:</span>
              <span className="text-2xl font-bold text-primary">
                {formatCurrency(calculateGrandTotal())}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBillItemsView = () => {
    if (!billingData) return null;

    return (
      <div className="space-y-6 bg-white p-6" style={{ minHeight: "800px" }}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Provisional Bill</h2>
          <p className="text-gray-600 mt-2">IPD Visit - {formatDate(new Date().toISOString())}</p>
        </div>

        {/* Pharmacy */}
        {billingData.pharmacy.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Pharmacy Orders</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Sale #</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Items</th>
                  <th className="text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.pharmacy.map((sale) => (
                  <tr key={sale.id} className="border-b">
                    <td className="p-2">{sale.saleNumber}</td>
                    <td className="p-2">{formatDate(sale.saleDate)}</td>
                    <td className="p-2">
                      {sale.items.map((item, idx) => (
                        <div key={idx} className="text-sm">
                          {item.name} × {item.quantity}
                        </div>
                      ))}
                    </td>
                <td className="p-2 text-right">{formatCurrency(sale.netAmount)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculatePharmacyTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Lab Orders */}
        {billingData.labOrders.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Lab Orders</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Order #</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Test</th>
                  <th className="text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.labOrders.map((order) => (
                  <tr key={order.id} className="border-b">
                    <td className="p-2">{order.orderNumber}</td>
                    <td className="p-2">{formatDate(order.orderDate)}</td>
                    <td className="p-2">
                      {order.items.map((item, idx) => (
                        <div key={idx}>{item.name}</div>
                      ))}
                    </td>
                    <td className="p-2 text-right">{formatCurrency(order.totalAmount)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateLabTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Services */}
        {billingData.services.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Visit Services</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Service</th>
                  <th className="text-left p-2">Quantity</th>
                  <th className="text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.services.map((service) => (
                  <tr key={service.id} className="border-b">
                    <td className="p-2">{service.name}</td>
                    <td className="p-2">{service.quantity}</td>
                    <td className="p-2 text-right">{formatCurrency(service.total)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={2} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateServicesTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Room Charges */}
        {billingData.roomCharges && billingData.roomCharges.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Room Charges</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Room</th>
                  <th className="text-left p-2">Period</th>
                  <th className="text-left p-2">Duration</th>
                  <th className="text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.roomCharges.map((room, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="p-2">
                      {room.roomName} ({room.roomType})
                    </td>
                    <td className="p-2 text-sm">
                      {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                    </td>
                    <td className="p-2 text-sm">
                      {Math.ceil(room.durationDays)} day(s)
                    </td>
                    <td className="p-2 text-right">{formatCurrency(room.charge)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateRoomChargesTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Grand Total */}
        <div className="mt-8 pt-4 border-t-2">
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold text-gray-900">Grand Total:</span>
            <span className="text-3xl font-bold text-primary">
              {formatCurrency(calculateGrandTotal())}
            </span>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            Print Bill
          </button>
        </div>
      </div>
    );
  };

  const renderPreInvoiceView = () => {
    if (!billingData) return null;
    const editableTotal = calculateEditableTotal();
    const hasItems =
      billingData.pharmacy.length > 0 ||
      billingData.labOrders.length > 0 ||
      billingData.services.length > 0 ||
      (billingData.roomCharges && billingData.roomCharges.length > 0);

    return (
      <div className="space-y-6">
        <div className="border rounded-lg p-4 bg-green-50 flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Unallocated Advance</div>
            <div className="text-xl font-bold text-green-700 mt-1">
              {formatCurrency(getAdvanceBalance())}
            </div>
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-yellow-50 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm text-gray-600">No bill generated for this visit</div>
            <div className="text-lg font-semibold text-gray-900">
              Collect advance or generate bill
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setIsAdvanceModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Collect Advance
            </button>
            <button
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice || !hasItems}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              {isGeneratingInvoice ? "Generating..." : "Generate Bill"}
            </button>
          </div>
        </div>

        <div className="border rounded-lg flex flex-col max-h-[60vh]">
          <div className="p-4 bg-gray-50 border-b shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">Edit Rates</h3>
          </div>
          <div className="p-4 space-y-3 flex-1 overflow-y-auto min-h-0">
            {billingData.pharmacy.map((sale) => {
              const key = `pharmacy-${sale.id}`;
              return (
                <div key={key} className="flex items-center justify-between gap-4 p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">Pharmacy Sale #{sale.saleNumber}</div>
                    <div className="text-sm text-gray-500">{formatDate(sale.saleDate)}</div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, sale.netAmount || 0)}
                    onChange={(e) =>
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-32 border rounded-lg px-3 py-2 text-right"
                  />
                </div>
              );
            })}

            {billingData.labOrders.map((order) => {
              const key = `lab-${order.id}`;
              return (
                <div key={key} className="flex items-center justify-between gap-4 p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">Lab Order #{order.orderNumber}</div>
                    <div className="text-sm text-gray-500">{formatDate(order.orderDate)}</div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, order.totalAmount || 0)}
                    onChange={(e) =>
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-32 border rounded-lg px-3 py-2 text-right"
                  />
                </div>
              );
            })}

            {billingData.services.map((service) => {
              const key = `service-${service.id}`;
              return (
                <div key={key} className="flex items-center justify-between gap-4 p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{service.name}</div>
                    <div className="text-sm text-gray-500">
                      Quantity: {service.quantity}
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, service.total || 0)}
                    onChange={(e) =>
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-32 border rounded-lg px-3 py-2 text-right"
                  />
                </div>
              );
            })}

            {billingData.roomCharges?.map((room, idx) => {
              const key = `room-${room.id}-${idx}`;
              return (
                <div key={key} className="flex items-center justify-between gap-4 p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">
                      {room.roomName} ({room.roomType})
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, room.charge || 0)}
                    onChange={(e) =>
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-32 border rounded-lg px-3 py-2 text-right"
                  />
                </div>
              );
            })}

            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-900">Custom Items</div>
                <button
                  onClick={addCustomItem}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Add Item
                </button>
              </div>

              {customItems.length === 0 && (
                <div className="text-sm text-gray-500">No custom items added.</div>
              )}

              {customItems.map((item) => (
                <div key={item.id} className="grid grid-cols-12 gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Item name"
                    value={item.name}
                    onChange={(e) => updateCustomItem(item.id, { name: e.target.value })}
                    className="col-span-6 border rounded-lg px-3 py-2"
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={item.quantity}
                    onChange={(e) =>
                      updateCustomItem(item.id, { quantity: parseInt(e.target.value, 10) || 0 })
                    }
                    className="col-span-2 border rounded-lg px-3 py-2 text-right"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.rate}
                    onChange={(e) =>
                      updateCustomItem(item.id, { rate: parseFloat(e.target.value) || 0 })
                    }
                    className="col-span-3 border rounded-lg px-3 py-2 text-right"
                  />
                  <button
                    onClick={() => removeCustomItem(item.id)}
                    className="col-span-1 text-red-600 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {!hasItems && (
              <div className="text-sm text-gray-500">No bill items available.</div>
            )}
          </div>
          <div className="p-4 border-t bg-gray-50 shrink-0 flex justify-between items-center">
            <span className="font-semibold text-gray-900">Bill Total</span>
            <span className="text-xl font-bold text-primary">
              {formatCurrency(editableTotal)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentsView = () => {
    if (!hasInvoice) {
      return renderPreInvoiceView();
    }
    const outstanding = calculateOutstanding();
    const unallocatedBalance = getAdvanceBalance();
    const selectedTotal = getSelectedItemsTotal();
    const invoiceStatus = invoiceData?.invoice?.status;
    const isInvoicePaid = invoiceStatus === "paid" || outstanding <= 0;

    return (
      <div className="space-y-6">
        {/* Balance Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border rounded-lg p-4 bg-blue-50">
            <div className="text-sm text-gray-600">Current Balance</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">
              {formatCurrency(calculateGrandTotal())}
            </div>
          </div>
          <div className="border rounded-lg p-4 bg-yellow-50">
            <div className="text-sm text-gray-600">Outstanding</div>
            <div className="text-2xl font-bold text-yellow-700 mt-1">
              {isInvoicePaid ? "Paid" : formatCurrency(outstanding)}
            </div>
          </div>
        </div>

        {isInvoicePaid && (
          <div className="border rounded-lg p-4 bg-green-50">
            <div className="text-sm text-gray-600">Invoice Status</div>
            <div className="text-lg font-semibold text-green-700 mt-1">
              Paid in full
            </div>
          </div>
        )}

        {/* Unallocated Balance */}
        <div className="border rounded-lg p-4 bg-green-50">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm text-gray-600">Unallocated Balance</div>
              <div className="text-xl font-bold text-green-700 mt-1">
                {formatCurrency(unallocatedBalance)}
              </div>
            </div>
            
          </div>
        </div>

        {!isInvoicePaid && (
          <div className="border rounded-lg flex flex-col max-h-[60vh]">
            <div className="p-4 bg-gray-50 border-b shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Select Items to Pay</h3>
            </div>
            <div className="p-4 space-y-4 flex-1 overflow-y-auto min-h-0">
              {billingData?.pharmacy.map((sale) => (
                <label
                  key={`pharmacy-${sale.id}`}
                  className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`pharmacy-${sale.id}`)}
                      onChange={() => handleItemSelection(`pharmacy-${sale.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">Pharmacy Sale #{sale.saleNumber}</div>
                      <div className="text-sm text-gray-500">{formatDate(sale.saleDate)}</div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(sale.netAmount)}</span>
                </label>
              ))}

              {billingData?.labOrders.map((order) => (
                <label
                  key={`lab-${order.id}`}
                  className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`lab-${order.id}`)}
                      onChange={() => handleItemSelection(`lab-${order.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">Lab Order #{order.orderNumber}</div>
                      <div className="text-sm text-gray-500">{formatDate(order.orderDate)}</div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(order.totalAmount)}</span>
                </label>
              ))}

              {billingData?.services.map((service) => (
                <label
                  key={`service-${service.id}`}
                  className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`service-${service.id}`)}
                      onChange={() => handleItemSelection(`service-${service.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">{service.name}</div>
                      <div className="text-sm text-gray-500">
                        Quantity: {service.quantity}
                      </div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(service.total)}</span>
                </label>
              ))}

              {billingData?.roomCharges?.map((room, idx) => (
                <label
                  key={`room-${room.id}-${idx}`}
                  className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`room-${room.id}-${idx}`)}
                      onChange={() => handleItemSelection(`room-${room.id}-${idx}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">
                        {room.roomName} ({room.roomType})
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                      </div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(room.charge)}</span>
                </label>
              ))}
            </div>

            {selectedItems.size > 0 && (
              <div className="p-4 border-t bg-gray-50 shrink-0">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold text-gray-900">Selected Total:</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(selectedTotal)}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => openPaymentModal("selected")}
                    disabled={isProcessingPayment}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
                  >
                    {isProcessingPayment ? "Processing..." : "Make Payment"}
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Implement new payment modal
                      showNotification("info", "New Payment", "Feature coming soon", "topRight");
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    New Payment
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment Allocations */}
        {invoiceData?.invoice?.paymentAllocations && (
          <div className="border rounded-lg">
            <div className="p-4 bg-gray-50 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Payment Allocations</h3>
            </div>
            <div className="p-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Receipt #</th>
                    <th className="text-left p-2">Amount</th>
                    <th className="text-left p-2">Allocated</th>
                    <th className="text-left p-2">Payment Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceData.invoice.paymentAllocations.map((alloc: any, idx: number) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2">
                        {formatDate(alloc.patientTransaction?.paymentDate)}
                      </td>
                      <td className="p-2">
                        {alloc.patientTransaction?.receiptNumber || "-"}
                      </td>
                      <td className="p-2">
                        {formatCurrency(alloc.patientTransaction?.amount)}
                      </td>
                      <td className="p-2">{formatCurrency(alloc.allocatedAmount)}</td>
                      <td className="p-2">{alloc.patientTransaction?.paymentMode || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Make Payment (Outstanding) */}
        {!isInvoicePaid && (
          <div className="flex justify-end">
            <button
              onClick={() => openPaymentModal("outstanding")}
              disabled={isProcessingPayment}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Make Payment
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div
        className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
        aria-hidden="true"
        onClick={onClose}
      ></div>

      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="inline-block w-full max-w-7xl h-[90vh] overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex justify-between p-4 text-white bg-primary">
            <span className="text-lg font-bold">IPD Visit Billing</span>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Main Content */}
          <div className="flex h-[calc(90vh-64px)]">
            {/* Left Navigation - 25% */}
            <div className="w-1/4 border-r bg-gray-50 overflow-y-auto">
              <nav className="p-4 space-y-2">
                <button
                  onClick={() => setActiveNav("summary")}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                    activeNav === "summary"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setActiveNav("billItems")}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                    activeNav === "billItems"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Bill Items
                </button>
                <button
                  onClick={() => {
                    setActiveNav("payments");
                    fetchPaymentData();
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                    activeNav === "payments"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Payments & Allocations
                </button>
              </nav>
            </div>

            {/* Right Content - 75% */}
            <div className="w-3/4 overflow-y-auto p-6">
              {isLoading ? (
                <LoaderComponent />
              ) : (
                <>
                  {activeNav === "summary" && renderSummaryView()}
                  {activeNav === "billItems" && renderBillItemsView()}
                  {activeNav === "payments" && renderPaymentsView()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isAdvanceModalOpen && (
        <PaymentModal
          isOpen={isAdvanceModalOpen}
          onClose={() => setIsAdvanceModalOpen(false)}
          total={0}
          discount={0}
          patientId={patientId}
          sourceType="visit"
          onSave={handleAdvanceSave}
          onPaymentSuccess={() => setIsAdvanceModalOpen(false)}
        />
      )}

      {isPaymentModalOpen && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          total={
            paymentContext === "selected"
              ? getSelectedItemsTotal()
              : calculateOutstanding()
          }
          discount={0}
          patientId={patientId}
          sourceType="visit"
          advanceBalance={getAdvanceBalance()}
          onSave={handleInvoicePaymentSave}
          onPaymentSuccess={() => setIsPaymentModalOpen(false)}
        />
      )}
    </div>
  );
};

export default IPDBillingModal;
